import { DEFAULT_NAMESPACE } from '../shared/constants';
import { ILogContext } from '../shared/types';

import { LogContext } from './LogContext';

/**
 * Manages global and namespace-specific logging context
 * Supports context isolation between namespaces with global context inheritance
 */
export class LoggerContextManager {
	private globalContext: LogContext;
	private namespaceContexts: Map<string, LogContext> = new Map();

	constructor(defaultContext?: Record<string, unknown>) {
		this.globalContext = new LogContext(defaultContext);
	}

	/**
	 * Set global context for all loggers
	 * @param context - Context fields to merge
	 */
	public setGlobalContext(context: Partial<ILogContext>): void {
		this.globalContext.merge(context);
	}

	/**
	 * Clear global context fields or all global context
	 * @param keys - Optional array of keys to clear
	 */
	public clearGlobalContext(keys?: string[]): void {
		this.globalContext.clear(keys);
	}

	/**
	 * Get global context
	 * @returns Copy of global context
	 */
	public getGlobalContext(): ILogContext {
		return this.globalContext.get();
	}

	/**
	 * Set context for a specific namespace
	 * @param context - Context fields to merge
	 * @param namespace - Namespace to set context for
	 */
	public setContext(
		context: Partial<ILogContext>,
		namespace = DEFAULT_NAMESPACE
	): void {
		const namespaceContext = this.getOrCreateNamespaceContext(namespace);
		namespaceContext.merge(context);
	}

	/**
	 * Clear context for a specific namespace
	 * @param keys - Optional array of keys to clear
	 * @param namespace - Namespace to clear context for
	 */
	public clearContext(keys?: string[], namespace = DEFAULT_NAMESPACE): void {
		const namespaceContext = this.namespaceContexts.get(namespace);

		if (namespaceContext) {
			namespaceContext.clear(keys);
		}
	}

	/**
	 * Get merged context (global + namespace)
	 * Namespace context overrides global context on conflicts
	 * @param namespace - Namespace to get context for
	 * @returns Merged context
	 */
	public getMergedContext(namespace = DEFAULT_NAMESPACE): ILogContext {
		const global = this.globalContext.get();

		const namespaceContext = this.namespaceContexts.get(namespace);

		if (!namespaceContext) {
			return global;
		}

		return { ...global, ...namespaceContext.get() };
	}

	/**
	 * Get or create namespace context
	 * @param namespace - Namespace name
	 * @returns LogContext for the namespace
	 */
	private getOrCreateNamespaceContext(namespace: string): LogContext {
		let context = this.namespaceContexts.get(namespace);
		if (!context) {
			context = new LogContext();
			this.namespaceContexts.set(namespace, context);
		}
		return context;
	}

	/**
	 * Reset all context
	 */
	public reset(): void {
		this.globalContext.clear();
		this.namespaceContexts.clear();
	}
}
